import { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, getStoredUser, setStoredUser, setToken } from "./api";
import { moduleEnabled } from "./features";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [features, setFeatures] = useState(null); // {version, myModules, modules}
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function boot() {
      if (!getToken()) { setBooting(false); return; }
      try {
        const { data } = await api.get("/api/auth/me");
        if (mounted) { setUser(data.user); setStoredUser(data.user); if (data.features) setFeatures(data.features); }
      } catch {
        if (mounted) { setUser(null); setStoredUser(null); setToken(null); }
      } finally {
        if (mounted) setBooting(false);
      }
    }
    boot();
    return () => { mounted = false; };
  }, []);

  // Pick up admin module-toggle changes when the tab regains focus.
  useEffect(() => {
    function onVis() {
      if (document.visibilityState !== "visible" || !getToken()) return;
      api.get("/api/config/features").then(({ data }) => setFeatures(data)).catch(() => {});
    }
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  function applySession({ accessToken, user, features }) {
    setToken(accessToken);
    setStoredUser(user);
    setUser(user);
    if (features) setFeatures(features);
  }

  function updateUser(u) {
    setStoredUser(u);
    setUser(u);
  }

  async function logout() {
    setToken(null); setStoredUser(null); setUser(null); setFeatures(null);
  }

  // Is a feature module visible to the current user? Core items (no key) always show.
  function isFeatureEnabled(key) {
    return moduleEnabled(features, key, user?.role);
  }

  return (
    <AuthCtx.Provider value={{ user, features, booting, applySession, updateUser, logout, isFeatureEnabled }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}
